import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom, Observable, Subject, Subscriber } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { ALL_FILTER_ITEM } from './filters-panel.component';
import { checkFilter, Filter, FilterID, FilterItem, FilterType } from './models/filter';
import { ServiceFeatures } from '../../models/serviceFeatures';
import { BoolSelectingStrategy, TreeCheckingStrategy, TreeSelectingStrategy } from './models/strategy';

import { SessionService } from '../../services/session.service';
import { Utils } from '../../services/utils.service';
import { CategoryKey } from '../../modals/welcome-demo-form-modal/welcome-demo-form-modal.component';

export type CategoriesParams = {
	ids?: string; // array of ids joined by ','
	search?: string;
	shops?: string; // array of ids joined by ','
	brands?: string; // array of ids joined by ','
	suppliers?: string; // array of ids joined by ','
	markers?: string; // array of ids joined by ','
}

export type BrandsParams = {
	search?: string;
	shops?: string; // array of ids joined by ','
	categories?: string; // array of ids joined by ','
	suppliers?: string; // array of ids joined by ','
	markers?: string; // array of ids joined by ','
}

export type MarkersParams = {
	ids?: string; // array of ids joined by ','
	shops?: string; // array of ids joined by ','
	search?: string;
	categories?: string; // array of ids joined by ','
	brands?: string; // array of ids joined by ','
	suppliers?: string; // array of ids joined by ','
}

export type SuppliersParams = {
	shops?: string; // array of ids joined by ','
	search?: string;
	categories?: string; // array of ids joined by ','
	brands?: string; // array of ids joined by ','
	markers?: string; // array of ids joined by ','
}

export type PlansParams = {
	locked?: boolean;
	shop_groups?: string; // array of ids joined by ','
	shop_markers?: string; // array of ids joined by ','
}

export type ShopMarkersParams = {
	ids?: string; // array of ids joined by ','
	search?: string;
}

@Injectable({
	providedIn: 'root'
})
export class FiltersService {

	private _cancelPreviousBrands$: Subject<void> = new Subject<void>();
	private _cancelPreviousMarkers$: Subject<void> = new Subject<void>();
	private _cancelPreviousPlans$: Subject<void> = new Subject<void>();
	private _cancelPreviousShops$: Subject<void> = new Subject<void>();
	private _cancelPreviousSuppliers$: Subject<void> = new Subject<void>();

	constructor(
		private http: HttpClient,
		private translate: TranslateService,
		private sessionService: SessionService
	) {}

	private _createTree(data: any, id: number): Array<any> {
		const tree = data.filter(m => m.pId == id) || [];
		tree.forEach(m => m.children = this._createTree(data, m.id));
		return tree;
	}

	private _getFilterItem(category: any): FilterItem {
		return {
			id: category.key,
			value: category.title,
			opened: false,
			hasChildren: !category.isLeaf,
			loading: false,
			children: category.children.map((child: any) => this._getFilterItem(child))
		}
	}

	getCategories(params: CategoriesParams = {}): Observable<Array<FilterItem>> {
		const queryParams = new HttpParams({ fromObject: params }).append('level', 2);

		return this.http.get('api/filters/categories-tree/', { params: queryParams }).pipe(
			map((response: any): Array<FilterItem> =>
				this._createTree(response.results, this.sessionService.defaults.root_category)
					.map((category: any): FilterItem => this._getFilterItem(category))
			)
		);
	}

	getCategoryChildren(id: number, params: CategoriesParams = {}): Observable<Array<FilterItem>> {
		const queryParams = new HttpParams({ fromObject: params });

		return this.http.get(`api/filters/categories-tree/${id}/`, { params: queryParams }).pipe(
			map((response: any): Array<FilterItem> =>
				this._createTree(response.results, id)
					.map((category: any): FilterItem => this._getFilterItem(category))
			)
		);
	}

	getBrands(params: BrandsParams = {}): Observable<Array<FilterItem>> {
		this._cancelPreviousBrands$.next();
		const queryParams: HttpParams = new HttpParams({ fromObject: params });

		return this.http.get('/api/filters/brands/', { params: queryParams }).pipe(
			takeUntil(this._cancelPreviousBrands$),
			map((response: any): Array<FilterItem> => {
				return response.map((brand: any): FilterItem => {
					return {
						id: brand.key,
						value: brand.title,
						opened: false,
						hasChildren: false,
						loading: false,
						children: []
					};
				});
			}));
	}

	getMarkers(params: MarkersParams = {}): Observable<Array<FilterItem>> {
		this._cancelPreviousMarkers$.next();
		const queryParams: HttpParams = new HttpParams({ fromObject: params }).append('level', 2);

		return this.http.get('/api/filters/markers-tree/', { params: queryParams }).pipe(
			takeUntil(this._cancelPreviousMarkers$),
			map((response: any): Array<FilterItem> =>
				this._createTree(response.results, this.sessionService.defaults.root_marker)
					.map((category: any): FilterItem => this._getFilterItem(category))
			));
	}

	getMarkerChildren(id: number, params: MarkersParams = {}): Observable<Array<FilterItem>> {
		const queryParams: HttpParams = new HttpParams({ fromObject: params });

		return this.http.get(`api/filters/markers-tree/${id}/`, { params: queryParams }).pipe(
			map((response: any): Array<FilterItem> =>
				this._createTree(response.results, id)
					.map((category: any): FilterItem => this._getFilterItem(category))
			)
		);
	}

	getSuppliers(params: SuppliersParams = {}): Observable<Array<FilterItem>> {
		this._cancelPreviousSuppliers$.next();
		const queryParams: HttpParams = new HttpParams({ fromObject: params });

		return this.http.get('/api/filters/suppliers/', { params: queryParams }).pipe(
			takeUntil(this._cancelPreviousSuppliers$),
			map((response: any): Array<FilterItem> => {
				return response.map((supplier: any): FilterItem => {
					return {
						id: supplier.key,
						value: supplier.title,
						opened: false,
						hasChildren: false,
						loading: false,
						children: []
					};
				});
			})
		);
	}

	getAssortmentTypes(): Observable<Array<FilterItem>> {
		type AssortmentType = {
			title: string;
			key: number;
			active: boolean;
		}
		return this.http.get('/api/filters/assortment-types/').pipe(
			map((response: Array<AssortmentType>): Array<FilterItem> => {
				if (!response?.length) return [];

				const activeTypes: Array<AssortmentType> = response.filter((status: AssortmentType) => status.active);
				const inactiveTypes: Array<AssortmentType> = response.filter((status: AssortmentType) => !status.active);

				const filters: Array<FilterItem> = [];
				if (activeTypes.length) {
					filters.push({
						id: activeTypes.map((status: AssortmentType) => status.key).join(','),
						value: this.translate.instant('COMMON.ASSORTMENT.ACTIVE_ONE'),
						opened: false,
						hasChildren: true,
						loading: false,
						children: activeTypes.map((status: AssortmentType) => ({
							id: status.key,
							value: status.title,
							opened: false,
							hasChildren: false,
							loading: false,
							children: []
						}))
					});
				}
				if (inactiveTypes.length) {
					filters.push({
						id: inactiveTypes.map((status: AssortmentType) => status.key).join(','),
						value: this.translate.instant('COMMON.ASSORTMENT.INACTIVE_ONE'),
						opened: false,
						hasChildren: true,
						loading: false,
						children: inactiveTypes.map((status: AssortmentType) => ({
							id: status.key,
							value: status.title,
							opened: false,
							hasChildren: false,
							loading: false,
							children: []
						}))
					});
				}

				return filters;
			}));
	}

	getProductStatuses(): Observable<Array<FilterItem>> {
		return this.http.get('/api/filters/product-shop-statuses/').pipe(
			map((response: Array<any>): Array<FilterItem> =>
				response.map((status: any) => ({
					id: status.key,
					value: status.title,
					opened: false,
					hasChildren: false,
					loading: false,
					children: []
				}))
			)
		);
	}

	getShopMarkers(params: ShopMarkersParams = {}): Observable<Array<FilterItem>> {
		const queryParams: HttpParams = new HttpParams({ fromObject: params }).append('level', 2);

		return this.http.get('/api/filters/shop-markers-tree/', { params: queryParams }).pipe(
			map((response: any): Array<FilterItem> =>
				this._createTree(response.results, 1)
					.map((marker: any): FilterItem => this._getFilterItem(marker))
			)
		);
	}

	getShopMarkerChildren(id: number): Observable<Array<FilterItem>> {
		const queryParams: HttpParams = new HttpParams({ fromObject: { } });

		return this.http.get(`/api/filters/shop-markers-tree/${id}/`, { params: queryParams }).pipe(
			map((response: any): Array<FilterItem> =>
				this._createTree(response.results, id)
					.map((marker: any): FilterItem => this._getFilterItem(marker))
			)
		);
	}

	getShopGroups(params: ShopMarkersParams = {}): Observable<Array<FilterItem>> {
		const queryParams: HttpParams = new HttpParams({ fromObject: params });

		return this.http.get('/api/filters/shop-groups-tree/', { params: queryParams }).pipe(
			map((response: any): Array<FilterItem> =>
				this._createTree(response.results, null)
					.map((group: any): FilterItem => this._getFilterItem(group))
			)
		);
	}

	getShopGroupChildren(id: number): Observable<Array<FilterItem>> {
		return this.http.get(`/api/filters/shop-groups-tree/${id}/`).pipe(
			map((response: any): Array<FilterItem> =>
				this._createTree(response.results, id)
					.map((category: any): FilterItem => this._getFilterItem(category))
			)
		);
	}

	getPlans(params: PlansParams = {}, fields: Array<string> = []): Observable<Array<FilterItem>> {
		this._cancelPreviousPlans$.next();
		const requestFields: Array<string> = ['id', 'name', ...fields];
		Utils.assignObject(params, { fields: requestFields.join(',') });

		const queryParams: HttpParams = new HttpParams({ fromObject: params });

		return this.http.get('api/v1/plans/', { params: queryParams }).pipe(
			takeUntil(this._cancelPreviousPlans$),
			map((response: any): Array<FilterItem> => {
				return response
					.map((plan: any): FilterItem => {
						return {
							id: plan.id,
							value: plan.name,
							opened: false,
							hasChildren: false,
							loading: false,
							children: []
						};
					})
					.sort(Utils.sortBy('value'));
			})
		);
	}

	getShops(params: PlansParams = {}): Observable<Array<FilterItem>> {
		this._cancelPreviousShops$.next();
		const queryParams: HttpParams = new HttpParams({ fromObject: params });

		return this.http.get('/api/filters/shops/', { params: queryParams }).pipe(
			takeUntil(this._cancelPreviousShops$),
			map((response: any): Array<FilterItem> => {
				return response.map((shop: any): FilterItem => {
					return {
						id: shop.key,
						value: shop.title,
						opened: false,
						hasChildren: false,
						loading: false,
						children: []
					};
				});
			})
		);
	}

	getEquipment(): Observable<Array<FilterItem>> {
		return this.http
			.get('api/layout/instruments/', {
				params: {
					detail: true,
					fields: 'id,name',
					empty: false
				}
			})
			.pipe(
				map((response: any): Array<FilterItem> => {
					return response
						.map((instrument: any): FilterItem => {
							return {
								id: instrument.id,
								value: instrument.name
							};
						})
						.sort(Utils.sortBy('value'));
				})
			);
	}

	getEquipmentProducers(): Observable<Array<FilterItem>> {
		return this.http.get('/api/v1/equipment-producers/').pipe(
			map((response: Array<{ name: string }>): Array<FilterItem> => {
				return response
					.map((producer: { name: string }): FilterItem => {
						return {
							id: producer.name,
							value: producer.name,
							opened: false,
							hasChildren: false,
							loading: false,
							children: []
						};
					})
					.sort(Utils.sortBy('value'));
			}));
	}

	getEquipmentTypes(params: { used?: boolean } = {}): Observable<Array<FilterItem>> {
		const queryParams = new HttpParams({ fromObject: params });

		return this.http.get('/api/v1/equipment-types/', { params: queryParams }).pipe(
			map((response: Array<{ id: number, name: string }>): Array<FilterItem> => {
				return response
					.map((producer: { id: number, name: string }): FilterItem => {
						return {
							id: producer.id,
							value: producer.name,
							opened: false,
							hasChildren: false,
							loading: false,
							children: []
						};
					})
					.sort(Utils.sortBy('value'));
			}));
	}

	getNotificationsTypes(): Observable<Array<FilterItem>> {
		return this.http.get('/api/v1/notification-types/').pipe(
			map((response: Array<{ name: string }>): Array<FilterItem> => {
				return response
					.map((type: { name: string; id: string }): FilterItem => {
						return {
							id: type.id,
							value: type.name,
							opened: false,
							hasChildren: false,
							loading: false,
							children: []
						};
					});
			}));
	}

	commonSearch(
		filter: Filter,
		original: Array<FilterItem>,
		allItemExists: boolean = true
	): Observable<Array<FilterItem>> {
		const onFilter = (item: FilterItem) => {
			if (
				Utils.searchItem(
					item,
					['value'],
					filter.searchQuery,
					this.sessionService.personalSettings.searchAlgorithm
				)
			) {
				return true;
			} else {
				if (item.children?.length) {
					item.children = item.children.filter((item: FilterItem) => onFilter(item));
					if (item.children.length) return true;
				}
				return false;
			}
		}

		return new Observable<Array<FilterItem>>((subscriber: Subscriber<Array<FilterItem>>) => {
			if (filter.searchQuery.length) {
				const searched: Array<FilterItem> = JSON.parse(JSON.stringify(original)).filter((item: FilterItem) =>
					onFilter(item)
				);
				const data: Array<FilterItem> = allItemExists ? [{ ...ALL_FILTER_ITEM, children: searched }] : searched;
				subscriber.next(searched.length ? data : []);
			} else {
				const data: Array<FilterItem> = allItemExists ? [{ ...ALL_FILTER_ITEM, children: original }] : original;
				subscriber.next(original.length ? data : []);
			}
		});
	}

	getCategoriesFilter(
		order: number,
		original: Array<FilterItem>,
		originalBrands: Array<FilterItem> = [],
		originalSuppliers: Array<FilterItem> = [],
		originalMarkers: Array<FilterItem> = [],
		shops: Array<number> = []
	): Filter {
		return {
			id: FilterID.Categories,
			type: FilterType.CheckboxTree,
			param: 'categories',
			label: 'CATEGORIES',
			displayOrder: order,
			data: [{ ...ALL_FILTER_ITEM, children: original }],
			selectingStrategy: new TreeSelectingStrategy(),
			checkingStrategy: new TreeCheckingStrategy(),
			searchQuery: '',
			loadChildren: (id: number) => this.getCategoryChildren(id),
			search: (filter: Filter) => {
				return new Observable<Array<FilterItem>>((subscriber: Subscriber<Array<FilterItem>>) => {
					if (filter.searchQuery.length) {
						this.getCategories({ search: filter.searchQuery })
							.subscribe((tree: Array<FilterItem>) => {
								subscriber.next(tree.length ? [{ ...ALL_FILTER_ITEM, children: tree }] : []);
							});
					} else {
						this.getCategories({ ids: filter.selectingStrategy.getSelected().filter((id: number) => id != -1).join(',') })
							.subscribe((tree: Array<FilterItem>) => {
								subscriber.next(tree.length ? [{ ...ALL_FILTER_ITEM, children: tree }] : []);
							});
					}
				});
			},
			afterCheck: (filters: Array<Filter>) => {
				const categories: Filter = filters.find((filter: Filter) => filter.id == FilterID.Categories),
					brands: Filter = filters.find((filter: Filter) => filter.id == FilterID.Brands),
					suppliers: Filter = filters.find((filter: Filter) => filter.id == FilterID.Suppliers),
					markers: Filter = filters.find((filter: Filter) => filter.id == FilterID.Markers),
					selectedCategories: Array<number> = categories?.selectingStrategy.getSelected().filter((id: number) => id != -1),
					selectedBrands: Array<number> = brands?.selectingStrategy.getSelected().filter((id: number) => id != -1),
					selectedSuppliers: Array<number> = suppliers?.selectingStrategy.getSelected().filter((id: number) => id != -1),
					selectedMarkers: Array<number> = markers?.selectingStrategy.getSelected().filter((id: number) => id != -1),
					brandsParams: BrandsParams = {},
					suppliersParams: SuppliersParams = {},
					markersParams: MarkersParams = {};

				if (!brands && !suppliers && !markers) return;
				if (selectedCategories && selectedCategories.length) {
					brandsParams.categories = selectedCategories.join(',');
					suppliersParams.categories = selectedCategories.join(',');
					markersParams.categories = selectedCategories.join(',');
				}
				if (selectedBrands && selectedBrands.length) {
					suppliersParams.brands = selectedBrands.join(',');
					markersParams.brands = selectedBrands.join(',');
				}
				if (shops.length) {
					suppliersParams.shops = shops.join(',');
				}
				if (selectedSuppliers && selectedSuppliers.length) {
					markersParams.suppliers = selectedSuppliers.join(',');
				}
				if (markers?.searchQuery) {
					markersParams.search = markers.searchQuery;
				}
				if (selectedMarkers && selectedMarkers.length) {
					markersParams.ids = selectedMarkers.join(',');
				}

				if (brands) {
					this.getBrands(brandsParams).subscribe((data: Array<FilterItem>) => {
						originalBrands.length = 0;
						originalBrands.push(...data);

						brands.search(brands).subscribe((searched: Array<FilterItem>) => {
							brands.data = searched;
						});

						brands.selectingStrategy.getSelected().forEach((id: number) => {
							if ((id == -1 && !data.length) || (id != -1 && !data.some((item: FilterItem) => item.id == id)))
								brands.selectingStrategy.removeSelected(id);
						});

						checkFilter(brands, brands.data, brands.selectingStrategy.getSelected());
					});
				}

				if (suppliers && !suppliers.disabled) {
					this.getSuppliers(suppliersParams).subscribe((data: Array<FilterItem>) => {
						originalSuppliers.length = 0;
						originalSuppliers.push(...data);

						suppliers.search(suppliers).subscribe((searched: Array<FilterItem>) => {
							suppliers.data = searched;
						});

						suppliers.selectingStrategy.getSelected().forEach((id: number) => {
							if ((id == -1 && !data.length) || (id != -1 && !data.some((item: FilterItem) => item.id == id)))
								suppliers.selectingStrategy.removeSelected(id);
						});

						checkFilter(suppliers, suppliers.data, suppliers.selectingStrategy.getSelected());
					});
				}

				if (markers) {
					this.getMarkers(markersParams).subscribe((data: Array<FilterItem>) => {
						originalMarkers.length = 0;
						originalMarkers.push(...data);

						const onFilter = (item: FilterItem, id: number) => {
							if (item.id === id) return true;
							if (item.children?.length) {
								return item.children.some((child: FilterItem) => onFilter(child, id));
							}
							return false;
						}

						markers.selectingStrategy.getSelected().forEach((id: number) => {
							if ((id == -1 && !data.length) || (id != -1 && !data.some((item: FilterItem) => onFilter(item, id)))) {
								markers.selectingStrategy.removeSelected(id);
								markers.checkingStrategy.setKey(id, false, false);
							}
						});

						checkFilter(markers, markers.data, markers.selectingStrategy.getSelected());
					});
				}
			}
		};
	}

	getBrandsFilter(
		order: number,
		original: Array<FilterItem>,
		originalSuppliers: Array<FilterItem> = [],
		originalMarkers: Array<FilterItem> = [],
		shops: Array<number> = []
	): Filter {
		return {
			id: FilterID.Brands,
			type: FilterType.CheckboxTree,
			param: 'brands',
			label: 'BRANDS',
			displayOrder: order,
			data: [{ ...ALL_FILTER_ITEM, children: original }],
			selectingStrategy: new TreeSelectingStrategy(),
			checkingStrategy: new TreeCheckingStrategy(),
			searchQuery: '',
			search: (filter: Filter) => this.commonSearch(filter, original),
			afterCheck: (filters: Array<Filter>) => {
				const categories: Filter = filters.find((filter: Filter) => filter.id == FilterID.Categories),
					brands: Filter = filters.find((filter: Filter) => filter.id == FilterID.Brands),
					suppliers: Filter = filters.find((filter: Filter) => filter.id == FilterID.Suppliers),
					markers: Filter = filters.find((filter: Filter) => filter.id == FilterID.Markers),
					selectedCategories: Array<number> = categories?.selectingStrategy.getSelected().filter((id: number) => id != -1),
					selectedBrands: Array<number> = brands?.selectingStrategy.getSelected().filter((id: number) => id != -1),
					selectedSuppliers: Array<number> = suppliers?.selectingStrategy.getSelected().filter((id: number) => id != -1),
					selectedMarkers: Array<number> = markers?.selectingStrategy.getSelected().filter((id: number) => id != -1),
					suppliersParams: SuppliersParams = {},
					markersParams: MarkersParams = {};

				if (!suppliers && !markers) return;
				if (selectedCategories && selectedCategories.length) {
					suppliersParams.categories = selectedCategories.join(',');
					markersParams.categories = selectedCategories.join(',');
				}
				if (selectedBrands && selectedBrands.length) {
					suppliersParams.brands = selectedBrands.join(',');
					markersParams.brands = selectedBrands.join(',');
				}
				if (selectedSuppliers && selectedSuppliers.length) {
					markersParams.suppliers = selectedSuppliers.join(',');
				}
				if (markers?.searchQuery) {
					markersParams.search = markers.searchQuery;
				}
				if (selectedMarkers && selectedMarkers.length) {
					markersParams.ids = selectedMarkers.join(',');
				}
				if (shops.length) {
					suppliersParams.shops = shops.join(',');
				}

				if (suppliers && !suppliers.disabled) {
					this.getSuppliers(suppliersParams).subscribe((data: Array<FilterItem>) => {
						originalSuppliers.length = 0;
						originalSuppliers.push(...data);

						suppliers.search(suppliers).subscribe((searched: Array<FilterItem>) => {
							suppliers.data = searched;
						});

						suppliers.selectingStrategy.getSelected().forEach((id: number) => {
							if ((id == -1 && !data.length) || (id != -1 && !data.some((item: FilterItem) => item.id == id)))
								suppliers.selectingStrategy.removeSelected(id);
						});

						checkFilter(suppliers, suppliers.data, suppliers.selectingStrategy.getSelected());
					});
				}

				if (markers) {
					this.getMarkers(markersParams).subscribe((data: Array<FilterItem>) => {
						originalMarkers.length = 0;
						originalMarkers.push(...data);

						const onFilter = (item: FilterItem, id: number) => {
							if (item.id === id) return true;
							if (item.children?.length) {
								return item.children.some((child: FilterItem) => onFilter(child, id));
							}
							return false;
						}

						markers.selectingStrategy.getSelected().forEach((id: number) => {
							if ((id == -1 && !data.length) || (id != -1 && !data.some((item: FilterItem) => onFilter(item, id)))) {
								markers.selectingStrategy.removeSelected(id);
								markers.checkingStrategy.setKey(id, false, false);
							}
						});

						checkFilter(markers, markers.data, markers.selectingStrategy.getSelected());
					});
				}
			}
		};
	}

	getSuppliersFilter(order: number, original: Array<FilterItem>, originalMarkers?: Array<FilterItem>): Filter {
		return {
			id: FilterID.Suppliers,
			type: FilterType.CheckboxTree,
			param: 'suppliers',
			label: 'SUPPLIERS',
			displayOrder: order,
			data: [{ ...ALL_FILTER_ITEM, children: original }],
			selectingStrategy: new TreeSelectingStrategy(),
			checkingStrategy: new TreeCheckingStrategy(),
			searchQuery: '',
			search: (filter: Filter) => this.commonSearch(filter, original),
			afterCheck: (filters: Array<Filter>) => {
				const categories: Filter = filters.find((filter: Filter) => filter.id == FilterID.Categories),
					brands: Filter = filters.find((filter: Filter) => filter.id == FilterID.Brands),
					suppliers: Filter = filters.find((filter: Filter) => filter.id == FilterID.Suppliers),
					markers: Filter = filters.find((filter: Filter) => filter.id == FilterID.Markers),
					selectedCategories: Array<number> = categories?.selectingStrategy.getSelected().filter((id: number) => id != -1),
					selectedBrands: Array<number> = brands?.selectingStrategy.getSelected().filter((id: number) => id != -1),
					selectedSuppliers: Array<number> = suppliers?.selectingStrategy.getSelected().filter((id: number) => id != -1),
					selectedMarkers: Array<number> = markers?.selectingStrategy.getSelected().filter((id: number) => id != -1),
					markersParams: MarkersParams = {};

				if (!markers) return;
				if (selectedCategories && selectedCategories.length) {
					markersParams.categories = selectedCategories.join(',');
				}
				if (selectedBrands && selectedBrands.length) {
					markersParams.brands = selectedBrands.join(',');
				}
				if (selectedSuppliers && selectedSuppliers.length) {
					markersParams.suppliers = selectedSuppliers.join(',');
				}
				if (markers?.searchQuery) {
					markersParams.search = markers.searchQuery;
				}
				if (selectedMarkers && selectedMarkers.length) {
					markersParams.ids = selectedMarkers.join(',');
				}

				if (markers) {
					this.getMarkers(markersParams).subscribe((data: Array<FilterItem>) => {
						originalMarkers.length = 0;
						originalMarkers.push(...data);

						const onFilter = (item: FilterItem, id: number) => {
							if (item.id === id) return true;
							if (item.children?.length) {
								return item.children.some((child: FilterItem) => onFilter(child, id));
							}
							return false;
						}

						markers.selectingStrategy.getSelected().forEach((id: number) => {
							if ((id == -1 && !data.length) || (id != -1 && !data.some((item: FilterItem) => onFilter(item, id)))) {
								markers.selectingStrategy.removeSelected(id);
								markers.checkingStrategy.setKey(id, false, false);
							}
						});

						checkFilter(markers, markers.data, markers.selectingStrategy.getSelected());
					});
				}
			}
		};
	}

	getMarkersFilter(order: number, original: Array<FilterItem>, filters: Array<Filter> = []): Filter {
		const originalBackup: Array<FilterItem> = JSON.parse(JSON.stringify(original));
		
		return {
			id: FilterID.Markers,
			type: FilterType.CheckboxTree,
			param: 'markers',
			label: 'CHARACTERISTIC',
			displayOrder: order,
			data: [{ ...ALL_FILTER_ITEM, children: original }],
			selectingStrategy: new TreeSelectingStrategy(),
			checkingStrategy: new TreeCheckingStrategy(),
			searchQuery: '',
			loadChildren: (id: number) => {
				const markersParams: MarkersParams = {};
				if (filters.length) {
					filters.forEach((f: Filter) => {
						const selected: Array<number> = f.selectingStrategy.getSelected().filter((id: number) => id != -1);
						if (selected?.length) markersParams[f.param] = selected.join(',');
					});
				}
				return this.getMarkerChildren(id, markersParams);
			},
			search: (filter: Filter) => {
				return new Observable<Array<FilterItem>>((subscriber: Subscriber<Array<FilterItem>>) => {
					const markersParams: MarkersParams = {};
					if (filters.length) {
						filters.forEach((f: Filter) => {
							const selected: Array<number> = f.selectingStrategy.getSelected().filter((id: number) => id != -1);
							if (selected?.length) markersParams[f.param] = selected.join(',');
						});
					}
					markersParams.ids = filter.selectingStrategy.getSelected().filter((id: number) => id != -1).join(',');

					if (filter.searchQuery.length) {
						markersParams.search = filter.searchQuery;
						this.getMarkers(markersParams)
							.subscribe((tree: Array<FilterItem>) => {
								if (tree.length) {
									original.length = 0;
									original.push(...tree);
									subscriber.next([{ ...ALL_FILTER_ITEM, children: original }]);
								} else {
									subscriber.next([]);
								}
							});
					} else {
						if (!original.length && originalBackup.length) {
							original.length = 0;
							original.push(...JSON.parse(JSON.stringify(originalBackup)));
						}
						subscriber.next(original.length ? [{ ...ALL_FILTER_ITEM, children: original }] : []);
					}
				});
			}
		};
	}

	getAssortmentTypesFilter(order: number, original: Array<FilterItem>): Filter {
		return {
			id: FilterID.AssortmentTypes,
			type: FilterType.CheckboxTree,
			param: 'assortment_types',
			label: 'ASSORTMENT_TYPE',
			displayOrder: order,
			data: [{ ...ALL_FILTER_ITEM, children: original }],
			selectingStrategy: new TreeSelectingStrategy(),
			checkingStrategy: new TreeCheckingStrategy(),
			searchQuery: '',
			search: (filter: Filter) => this.commonSearch(filter, original)
		};
	}

	getArchivedProductsFilter(order: number): Filter {
		return {
			id: FilterID.Radio,
			type: FilterType.Radio,
			param: 'is_archived',
			label: 'ASSORTMENT',
			displayOrder: order,
			data: [
				{ id: -1, value: 'ALL_PRODUCTS' },
				{ id: 0, value: 'COMMON.PRODUCT.UNARCHIVED_PRODUCTS' },
				{ id: 1, value: 'COMMON.PRODUCT.ARCHIVED_PRODUCTS' }
			],
			selectingStrategy: new BoolSelectingStrategy()
		}
	}

	getProductStatusesFilter(order: number, original: Array<FilterItem>): Filter {
		return {
			id: FilterID.ProductStatuses,
			type: FilterType.CheckboxTree,
			param: 'product_statuses',
			label: 'PRODUCT_STATUS_TYPE',
			displayOrder: order,
			data: [{ ...ALL_FILTER_ITEM, children: original }],
			selectingStrategy: new TreeSelectingStrategy(),
			checkingStrategy: new TreeCheckingStrategy(),
			searchQuery: '',
			search: (filter: Filter) => this.commonSearch(filter, original)
		};
	}

	getStoreMarkersFilter(order: number, original: Array<FilterItem>, originalData: Array<FilterItem>): Filter {
		return {
			id: FilterID.StoreMarkers,
			type: FilterType.CheckboxTree,
			param: 'shop_markers',
			label: 'SHOP-FORMAT',
			displayOrder: order,
			data: [{ ...ALL_FILTER_ITEM, children: original }],
			selectingStrategy: new TreeSelectingStrategy(),
			checkingStrategy: new TreeCheckingStrategy(),
			searchQuery: '',
			loadChildren: (id: number) => this.getShopMarkerChildren(id),
			search: (filter: Filter) => {
				return new Observable<Array<FilterItem>>((subscriber: Subscriber<Array<FilterItem>>) => {
					if (filter.searchQuery.length) {
						this.getShopMarkers({ search: filter.searchQuery })
							.subscribe((tree: Array<FilterItem>) => {
								subscriber.next(tree.length ? [{ ...ALL_FILTER_ITEM, children: tree }] : []);
							});
					} else {
						this.getShopMarkers({ ids: filter.selectingStrategy.getSelected().filter((id: number) => id != -1).join(',') })
							.subscribe((tree: Array<FilterItem>) => {
								subscriber.next(tree.length ? [{ ...ALL_FILTER_ITEM, children: tree }] : []);
							});
					}
				});
			},
			afterCheck: (filters: Array<Filter>) => {
				const markers: Filter = filters.find((filter: Filter) => filter.id == FilterID.StoreMarkers),
					groups: Filter = filters.find((filter: Filter) => filter.id == FilterID.StoreGroups),
					plans: Filter = filters.find((filter: Filter) => filter.id == FilterID.StorePlans),
					shops: Filter = filters.find((filter: Filter) => filter.id == FilterID.Stores),
					selectedMarkers: Array<number> = markers?.selectingStrategy.getSelected().filter((id: number) => id != -1),
					selectedGroups: Array<number> = groups?.selectingStrategy.getSelected().filter((id: number) => id != -1),
					params: any = {};

				if (selectedMarkers && selectedMarkers.length) params.shop_markers = selectedMarkers.join(',');
				if (selectedGroups && selectedGroups.length) params.shop_groups = selectedGroups.join(',');

				if (plans) {
					this.getPlans(params).subscribe((data: Array<FilterItem>) => {
						originalData.length = 0;
						originalData.push(...data);

						plans.search(plans).subscribe((searched: Array<FilterItem>) => {
							plans.data = searched;
						});

						plans.selectingStrategy.getSelected().forEach((id: number) => {
							if ((id == -1 && !data.length) || (id != -1 && !data.some((item: FilterItem) => item.id == id)))
								plans.selectingStrategy.removeSelected(id);
						});

						checkFilter(plans, plans.data, plans.selectingStrategy.getSelected());
					});
				}

				if (shops) {
					this.getShops(params).subscribe((data: Array<FilterItem>) => {
						originalData.length = 0;
						originalData.push(...data);

						shops.search(shops).subscribe((searched: Array<FilterItem>) => {
							shops.data = searched;
						});

						shops.selectingStrategy.getSelected().forEach((id: number) => {
							if ((id == -1 && !data.length) || (id != -1 && !data.some((item: FilterItem) => item.id == id)))
								shops.selectingStrategy.removeSelected(id);
						});

						checkFilter(shops, shops.data, shops.selectingStrategy.getSelected());
					});
				}
			}
		};
	}

	getStoreGroupsFilter(order: number, original: Array<FilterItem>, originalData: Array<FilterItem>): Filter {
		return {
			id: FilterID.StoreGroups,
			type: FilterType.CheckboxTree,
			param: 'shop_groups',
			label: 'SHOP-GROUP',
			displayOrder: order,
			data: [{ ...ALL_FILTER_ITEM, children: original }],
			selectingStrategy: new TreeSelectingStrategy(),
			checkingStrategy: new TreeCheckingStrategy(),
			searchQuery: '',
			loadChildren: (id: number) => this.getShopGroupChildren(id),
			search: (filter: Filter) => {
				return new Observable<Array<FilterItem>>((subscriber: Subscriber<Array<FilterItem>>) => {
					if (filter.searchQuery.length) {
						this.getShopGroups({ search: filter.searchQuery })
							.subscribe((tree: Array<FilterItem>) => {
								subscriber.next(tree.length ? [{ ...ALL_FILTER_ITEM, children: tree }] : []);
							});
					} else {
						this.getShopGroups({ ids: filter.selectingStrategy.getSelected().filter((id: number) => id != -1).join(',') })
							.subscribe((tree: Array<FilterItem>) => {
								subscriber.next(tree.length ? [{ ...ALL_FILTER_ITEM, children: tree }] : []);
							});
					}
				});
			},
			afterCheck: (filters: Array<Filter>) => {
				const markers: Filter = filters.find((filter: Filter) => filter.id == FilterID.StoreMarkers),
					groups: Filter = filters.find((filter: Filter) => filter.id == FilterID.StoreGroups),
					plans: Filter = filters.find((filter: Filter) => filter.id == FilterID.StorePlans),
					shops: Filter = filters.find((filter: Filter) => filter.id == FilterID.Stores),
					selectedMarkers: Array<number> = markers?.selectingStrategy.getSelected().filter((id: number) => id != -1),
					selectedGroups: Array<number> = groups?.selectingStrategy.getSelected().filter((id: number) => id != -1),
					params: any = {};

				if (selectedMarkers && selectedMarkers.length) params.shop_markers = selectedMarkers.join(',');
				if (selectedGroups && selectedGroups.length) params.shop_groups = selectedGroups.join(',');

				if (plans) {
					this.getPlans(params).subscribe((data: Array<FilterItem>) => {
						originalData.length = 0;
						originalData.push(...data);

						plans.search(plans).subscribe((searched: Array<FilterItem>) => {
							plans.data = searched;
						});

						plans.selectingStrategy.getSelected().forEach((id: number) => {
							if ((id == -1 && !data.length) || (id != -1 && !data.some((item: FilterItem) => item.id == id)))
								plans.selectingStrategy.removeSelected(id);
						});

						checkFilter(plans, plans.data, plans.selectingStrategy.getSelected());
					});
				}

				if (shops) {
					this.getShops(params)
						.subscribe((data: Array<FilterItem>) => {
							originalData.length = 0;
							originalData.push(...data);

							shops.search(shops).subscribe((searched: Array<FilterItem>) => {
								shops.data = searched;
							});

							shops.selectingStrategy.getSelected().forEach((id: number) => {
								if ((id == -1 && !data.length) || (id != -1 && !data.some((item: FilterItem) => item.id == id)))
									shops.selectingStrategy.removeSelected(id);
							});

							checkFilter(shops, shops.data, shops.selectingStrategy.getSelected());
						});
				}
			}
		};
	}

	getStorePlansFilter(order: number, original: Array<FilterItem>): Filter {
		return {
			id: FilterID.StorePlans,
			type: FilterType.CheckboxTree,
			param: 'plans',
			label: 'COMMON.ENTITY.STORE_PLANS',
			displayOrder: order,
			data: [{ ...ALL_FILTER_ITEM, children: original }],
			selectingStrategy: new TreeSelectingStrategy(),
			checkingStrategy: new TreeCheckingStrategy(),
			searchQuery: '',
			search: (filter: Filter) => this.commonSearch(filter, original)
		};
	}

	getStoresFilter(order: number, original: Array<FilterItem>): Filter {
		return {
			id: FilterID.Stores,
			type: FilterType.CheckboxTree,
			param: 'shops',
			label: 'SHOPS',
			displayOrder: order,
			data: [{ ...ALL_FILTER_ITEM, children: original }],
			selectingStrategy: new TreeSelectingStrategy(),
			checkingStrategy: new TreeCheckingStrategy(),
			searchQuery: '',
			search: (filter: Filter) => this.commonSearch(filter, original)
		};
	}

	getEquipmentTypesFilter(
		order: number,
		original: Array<FilterItem>,
		param: string = 'types'
	): Filter {
		return {
			id: FilterID.EquipmentTypes,
			type: FilterType.CheckboxTree,
			param: param,
			label: 'EQUIPMENT_TYPE',
			displayOrder: order,
			data: [{ ...ALL_FILTER_ITEM, children: original }],
			selectingStrategy: new TreeSelectingStrategy(),
			checkingStrategy: new TreeCheckingStrategy(),
			searchQuery: '',
			search: (filter: Filter) => this.commonSearch(filter, original)
		};
	}

	getEquipmentProducersFilter(order: number, original: Array<FilterItem>): Filter {
		return {
			id: FilterID.EquipmentProducers,
			type: FilterType.CheckboxTree,
			param: 'producers',
			label: 'EQUIPMENT_PRODUCER',
			displayOrder: order,
			data: [{ ...ALL_FILTER_ITEM, children: original }],
			selectingStrategy: new TreeSelectingStrategy(),
			checkingStrategy: new TreeCheckingStrategy(),
			searchQuery: '',
			search: (filter: Filter) => this.commonSearch(filter, original)
		};
	}

	getNotificationsTypesFilter(order: number, original: Array<FilterItem>): Filter {
		return {
			id: FilterID.NotificationsTypes,
			type: FilterType.CheckboxTree,
			param: 'types',
			label: 'NOTIFICATIONS.TYPES',
			displayOrder: order,
			data: [{ ...ALL_FILTER_ITEM, children: original }],
			selectingStrategy: new TreeSelectingStrategy(),
			checkingStrategy: new TreeCheckingStrategy(),
			searchQuery: '',
			search: (filter: Filter) => this.commonSearch(filter, original)
		};
	}

	getIsMasterPositionFilter(order: number): Filter {
		return {
			id: FilterID.Radio,
			type: FilterType.Radio,
			param: 'is_master_position',
			label: 'COMMON.PRODUCT.FORMAT',
			displayOrder: order,
			data: [
				{ id: -1, value: 'ALL' },
				{ id: 0, value: 'PRODUCTS' },
				{ id: 1, value: 'MASTER_POSITION.PLURAL_TITLE' }
			],
			selectingStrategy: new BoolSelectingStrategy()
		}
	}

	getIsMasterPositionExpandedFilter(order: number): Filter {
		const data: Array<FilterItem> = [
			{
				id: 'mp_products,non_mp_products',
				value: this.translate.instant('PRODUCTS'),
				children: [
					{
						id: 'mp_products',
						value: this.translate.instant('COMMON.PRODUCT.PRODUCTS_IN_MP'),
						children: [],
						hasChildren: false
					},
					{
						id: 'non_mp_products',
						value: this.translate.instant('COMMON.PRODUCT.PRODUCTS_NOT_IN_MP'),
						children: [],
						hasChildren: false
					}
				],
				opened: false,
				hasChildren: true
			},
			{
				id: 'mp',
				value: this.translate.instant('MASTER_POSITION.PLURAL_TITLE'),
				children: [],
				opened: false,
				hasChildren: false
			}
		];

		return {
			id: FilterID.IsMasterPositionExpanded,
			type: FilterType.CheckboxTree,
			param: 'product_format',
			label: 'COMMON.PRODUCT.FORMAT',
			displayOrder: order,
			data: [{ ...ALL_FILTER_ITEM, children: data }],
			selectingStrategy: new TreeSelectingStrategy(),
			checkingStrategy: new TreeCheckingStrategy(),
			searchQuery: '',
			search: (filter: Filter) => this.commonSearch(filter, data)
		}
	}

	getPlanogramStatusesFilter(order: number, original: Array<FilterItem>): Filter {
		return {
			id: FilterID.PlanogramStatuses,
			type: FilterType.CheckboxTree,
			param: 'status',
			label: 'PLANOGRAM_STATUS',
			displayOrder: order,
			data: [{ ...ALL_FILTER_ITEM, children: original }],
			selectingStrategy: new TreeSelectingStrategy(),
			checkingStrategy: new TreeCheckingStrategy(),
			searchQuery: '',
			search: (filter: Filter) => this.commonSearch(filter, original)
		}
	}

	getProductsHideOptionsFilter(order: number, original: Array<FilterItem>): Filter {
		return {
			id: FilterID.ProductsHideOptions,
			type: FilterType.CheckboxTree,
			param: 'hide_options',
			label: 'ASSORTMENT',
			displayOrder: order,
			data: [{ ...ALL_FILTER_ITEM, children: original }],
			selectingStrategy: new TreeSelectingStrategy(),
			checkingStrategy: new TreeCheckingStrategy(),
			searchQuery: '',
			search: (filter: Filter) => this.commonSearch(filter, original)
		}
	}

	getDemoPlanogramCategoriesFilter(order: number, original: Array<FilterItem>): Filter {
		return {
			id: FilterID.DemoPlanogramCategories,
			type: FilterType.CheckboxTree,
			param: 'ids',
			label: 'DEMO.PLANOGRAM_PRODUCT_CATEGORIES',
			displayOrder: order,
			data: [{ ...ALL_FILTER_ITEM, children: original }],
			selectingStrategy: new TreeSelectingStrategy(),
			checkingStrategy: new TreeCheckingStrategy(),
			searchQuery: '',
			search: (filter: Filter) => this.commonSearch(filter, original)
		};
	}

	getDemoPlanogramCategories(): Observable<Array<FilterItem>> {
		return this.http.get('/api/filters/demo-planogram-categories/').pipe(
			map((response: Array<{ id: string; name: string }>): Array<FilterItem> =>
				response.map(item => ({
					id: item.id,
					value: this.translate.instant(CategoryKey[item.name]),
					opened: false,
					hasChildren: false,
					loading: false,
					children: []
				}))
			)
		);
	}

	async loadProductsHideOptionsTranslates(): Promise<Array<FilterItem>> {
		const translates = await lastValueFrom(this.translate.get([
			'HIDE_LAYOUT_PRODUCTS',
			'HIDE_ZERO_STOCKS_PRODUCTS',
			'HIDE_WITHOUT_SALES_PRODUCTS',
			'HIDE_WITHOUT_DIMENSIONS_PRODUCTS',
            'HIDE_PRODUCTS_IN_MASTER_POSITIONS',
            'HIDE_PRODUCTS_IN_LAID_MASTER_POSITIONS'
		]));

		const filterItems: Array<FilterItem> = [];

		filterItems.push({
			id: 'laid_products',
			value: translates['HIDE_LAYOUT_PRODUCTS'],
			children: [],
			opened: false,
			hasChildren: false,
			infoLabel: 'HIDE_LAYOUT_HELPER'
		});

		if (this.sessionService.hasFeature(ServiceFeatures.ProductStock)) {
			filterItems.push({
				id: 'zero_stock_products',
				value: translates['HIDE_ZERO_STOCKS_PRODUCTS'],
				children: [],
				opened: false,
				hasChildren: false,
				infoLabel: 'HIDE_ZERO_STOCKS_HELPER'
			});
		}

		if (this.sessionService.hasFeature(ServiceFeatures.Receipts)) {
			filterItems.push({
				id: 'products_without_sales',
				value: translates['HIDE_WITHOUT_SALES_PRODUCTS'],
				children: [],
				opened: false,
				hasChildren: false,
				infoLabel: 'HIDE_WITHOUT_SALES_HELPER'
			});
		}

		filterItems.push({
			id: 'products_without_sizes',
			value: translates['HIDE_WITHOUT_DIMENSIONS_PRODUCTS'],
			children: [],
			opened: false,
			hasChildren: false,
			infoLabel: 'HIDE_WITHOUT_DIMENSIONS_HELPER'
		});

        if (this.sessionService.hasFeature(ServiceFeatures.MasterPositions)) {
            filterItems.push({
                id: 'products_in_master_positions',
                value: translates['HIDE_PRODUCTS_IN_MASTER_POSITIONS'],
                children: [],
                opened: false,
				hasChildren: false,
				infoLabel: 'HIDE_PRODUCTS_IN_MP_HELPER'
            });

            filterItems.push({
                id: 'products_in_laid_master_positions',
                value: translates['HIDE_PRODUCTS_IN_LAID_MASTER_POSITIONS'],
                children: [],
                opened: false,
				hasChildren: false,
				infoLabel: 'HIDE_PRODUCTS_IN_LAID_MP_HELPER'
            });
        }

		return filterItems;
	}

	async loadStatusTranslates(inEditingStatus: boolean = false): Promise<Array<{ id: string, name?: string }>> {
		const statuses: Array<{ id: string; name?: string }> = [
				{ id: 'in_progress' },
				{ id: 'scheduled_execution' },
				{ id: 'executing' },
				{ id: 'executed' },
				{ id: 'returned' },
				{ id: 'checked' }
			],
			translates = await this.translate
				.get(statuses.map(s =>
					inEditingStatus && s.id === 'in_progress' ? 'IN_EDITING' : s.id.toUpperCase()
				))
				.toPromise();

		statuses.forEach(s =>
			s.name = translates[inEditingStatus && s.id === 'in_progress' ? 'IN_EDITING' : s.id.toUpperCase()]
		);

		return statuses;
	}
}